// Per-tab analytics session id. Stored in sessionStorage (so it's tab-scoped,
// matching the derive-sessions-in-the-read-layer model) and rotated after 30
// minutes of inactivity. Session boundaries + durations are reconstructed in
// the read layer from session_id + event gaps — nothing is stored server-side
// per session.

const KEY = 'dashone.analytics.session';
const IDLE_MS = 30 * 60 * 1000;

interface StoredSession {
  id: string;
  last: number;
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Return the current session id, minting a fresh one if none exists or if the
 * last activity was more than 30 minutes ago. Bumps the last-activity stamp on
 * every call. Falls back to an in-memory id if sessionStorage is unavailable.
 */
export function getSessionId(nowMs: number = Date.now()): string {
  let s: StoredSession | null = null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) s = JSON.parse(raw) as StoredSession;
  } catch {
    s = null;
  }

  if (!s || typeof s.id !== 'string' || typeof s.last !== 'number' || nowMs - s.last > IDLE_MS) {
    s = { id: newId(), last: nowMs };
  } else {
    s.last = nowMs;
  }

  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode / disabled storage — the id is still returned this call */
  }
  return s.id;
}
